// Home-page layout config: which pane is front-and-center.
//
//   "notes-center"    — notes list is front and center, Superdoc in the right
//                       pane (default).
//   "superdoc-center" — Superdoc is front and center, the single-note view sits
//                       in the right pane, and the notes list lives in the
//                       sidebar (the pre-swap arrangement).
//
// Flip it three ways (in priority order):
//   1. URL param  ?layout=superdoc-center   (also persists to localStorage)
//   2. localStorage key "superdoc-layout"
//   3. DEFAULT_SUPERDOC_LAYOUT below (the shipped default)
export type SuperdocLayout = "notes-center" | "superdoc-center";

export const DEFAULT_SUPERDOC_LAYOUT: SuperdocLayout = "notes-center";

const LAYOUT_STORAGE_KEY = "superdoc-layout";

const isSuperdocLayout = (value: string | null): value is SuperdocLayout =>
  value === "notes-center" || value === "superdoc-center";

/** Resolve the layout from URL param → localStorage → default. */
export const getInitialSuperdocLayout = (): SuperdocLayout => {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get("layout");
    if (isSuperdocLayout(fromUrl)) {
      localStorage.setItem(LAYOUT_STORAGE_KEY, fromUrl);
      return fromUrl;
    }
    const saved = localStorage.getItem(LAYOUT_STORAGE_KEY);
    if (isSuperdocLayout(saved)) {
      return saved;
    }
  } catch {
    // window / localStorage unavailable — fall through to the default.
  }
  return DEFAULT_SUPERDOC_LAYOUT;
};

/** Read a layout override off the current URL (?layout=…), if any. */
export const readSuperdocLayoutFromUrl = (): SuperdocLayout | null => {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get("layout");
    return isSuperdocLayout(fromUrl) ? fromUrl : null;
  } catch {
    return null;
  }
};

export const persistSuperdocLayout = (mode: SuperdocLayout): void => {
  try {
    localStorage.setItem(LAYOUT_STORAGE_KEY, mode);
  } catch {
    // ignore — persistence is best-effort
  }
};
